import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { savePermitForm, updatePermitForm } from '../../store/slices/projectPermitsSlice';
import { showToast } from '../utils/Toast';
import { fetchProjectData, prefillFormData } from '../../utils/projectDataUtils';
import { printPermitForm, createCompanyHeader, formatFormField, formatTableRow } from '../../utils/PermitPrintUtils';
import FloatingActionBar from './FloatingActionBar';

const ALXMaterialForm = ({ projectId, currentPermit, onClose }) => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  
  // Define all the form fields based on the image
  const [formData, setFormData] = useState({
    customer: '',
    jobName: '',
    jobAddress: '',
    jobNumber: '',
    totalSquareFootage: '',
    products: {
      sheetMembrane: [
        { description: 'Sheet Membrane and Lath', coverage: '-', jobSqFt: '', materialNeeded: '', costPerProduct: '', optionalCost: '#DIV/0!', totalNeeded: { value: 'WP-40', amount: 0, unit: 'rolls' } },
        { description: '', coverage: '-', jobSqFt: '', materialNeeded: '', costPerProduct: '', optionalCost: '#DIV/0!', totalNeeded: { value: 'WP-25', amount: 0, unit: 'pieces' } },
        { description: '', coverage: '-', jobSqFt: '', materialNeeded: '', costPerProduct: '', optionalCost: '#DIV/0!', totalNeeded: { value: 'WP-10', amount: 0.0, unit: 'box' } },
        { description: '', coverage: '-', jobSqFt: '', materialNeeded: '', costPerProduct: '', optionalCost: '', totalNeeded: { value: 'TC-1', amount: 0.0, unit: 'bags' } },
        { description: '', coverage: '-', jobSqFt: '', materialNeeded: '', costPerProduct: '', optionalCost: '', totalNeeded: { value: 'WP-81', amount: 0.0, unit: 'gallons' } }
      ],
      baseCoat: [
        { description: 'Base Coat', coverage: '-', jobSqFt: '', materialNeeded: '', costPerProduct: '', optionalCost: '#DIV/0!', totalNeeded: { value: 'TC-1', amount: 0.0, unit: 'bags' } },
        { description: '', coverage: '-', jobSqFt: '', materialNeeded: '', costPerProduct: '', optionalCost: '#DIV/0!', totalNeeded: { value: 'SC-10', amount: 0.0, unit: 'gallons' } }
      ],
      slurryCoat: [
        { description: 'Slurry Coat', coverage: '-', jobSqFt: '', materialNeeded: '', costPerProduct: '', optionalCost: '', totalNeeded: { value: '', amount: '', unit: '' } },
        { description: '', coverage: '-', jobSqFt: '', materialNeeded: '', costPerProduct: '', optionalCost: '#DIV/0!', totalNeeded: { value: '', amount: '', unit: '' } },
        { description: '', coverage: '-', jobSqFt: '', materialNeeded: '', costPerProduct: '', optionalCost: '#DIV/0!', totalNeeded: { value: '', amount: '', unit: '' } }
      ],
      textureCoat: [
        { description: 'Texture Coat', coverage: '-', jobSqFt: '', materialNeeded: '', costPerProduct: '', optionalCost: '', totalNeeded: { value: '', amount: '', unit: '' } },
        { description: '', coverage: '-', jobSqFt: '', materialNeeded: '', costPerProduct: '', optionalCost: '#DIV/0!', totalNeeded: { value: '', amount: '', unit: '' } },
        { description: '', coverage: '-', jobSqFt: '', materialNeeded: '', costPerProduct: '', optionalCost: '#DIV/0!', totalNeeded: { value: '', amount: '', unit: '' } }
      ],
      topCoat: [
        { description: 'Top Coat', coverage: '-', jobSqFt: '', materialNeeded: '', costPerProduct: '', optionalCost: '', totalNeeded: { value: '', amount: '', unit: '' } },
        { description: '', coverage: '-', jobSqFt: '', materialNeeded: '', costPerProduct: '', optionalCost: '#DIV/0!', totalNeeded: { value: '', amount: '', unit: '' } },
        { description: '', coverage: '-', jobSqFt: '', materialNeeded: '', costPerProduct: '', optionalCost: '#DIV/0!', totalNeeded: { value: '', amount: '', unit: '' } }
      ]
    },
    totalCosts: {
      'WP-40': 0,
      'WP-25': 0,
      'WP-10': 0,
      'TC-1': 0,
      'WP-81': 0,
      'TC-2': 0,
      'SC-10': 0
    },
    grandTotal: 0
  });

  useEffect(() => {
    if (currentPermit && currentPermit.formType === 'alxMaterial') {
      setFormData(currentPermit.formData || formData);
    } else {
      // Fetch project data to prefill the form
      const getProjectData = async () => {
        try {
          const projectData = await fetchProjectData(projectId);
          const prefilled = prefillFormData(projectData, formData);
          setFormData(prefilled);
        } catch (error) {
          console.error('Error prefilling form data:', error);
        }
      };
      
      getProjectData();
    }
  }, [currentPermit, projectId]);

  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleProductChange = (category, index, field, value) => {
    setFormData(prev => {
      const updatedProducts = { ...prev.products };
      updatedProducts[category][index] = {
        ...updatedProducts[category][index],
        [field]: value
      };
      return {
        ...prev,
        products: updatedProducts
      };
    });
  };

  const handleTotalNeededChange = (category, index, field, value) => {
    setFormData(prev => {
      const updatedProducts = { ...prev.products };
      updatedProducts[category][index] = {
        ...updatedProducts[category][index],
        totalNeeded: {
          ...updatedProducts[category][index].totalNeeded,
          [field]: value
        }
      };
      return {
        ...prev,
        products: updatedProducts
      };
    });
  };

  const handleTotalCostChange = (key, value) => {
    setFormData(prev => ({
      ...prev,
      totalCosts: {
        ...prev.totalCosts,
        [key]: value
      }
    }));
  };

  // Calculate totals whenever relevant fields change
  const calculateTotals = useCallback(() => {
    let grandTotal = 0;
    
    // Calculate total costs for each product type
    Object.keys(formData.totalCosts).forEach(key => {
      const cost = parseFloat(formData.totalCosts[key]) || 0;
      grandTotal += cost;
    });

    setFormData(prev => ({
      ...prev,
      grandTotal
    }));
  }, [formData.totalCosts]);

  useEffect(() => {
    calculateTotals();
  }, [formData.totalCosts, calculateTotals]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (currentPermit && currentPermit._id) {
        await dispatch(updatePermitForm({
          projectId,
          permitId: currentPermit._id,
          formData: {
            ...currentPermit,
            formData
          }
        })).unwrap();
        showToast('ALX Material form updated successfully', 'success');
      } else {
        await dispatch(savePermitForm({
          projectId,
          formType: 'alxMaterial',
          formData
        })).unwrap();
        showToast('ALX Material form saved successfully', 'success');
      }
      onClose();
    } catch (error) {
      showToast(error.message || 'Failed to save ALX Material form', 'error');
    } finally {
      setLoading(false);
    }
  };

  const renderProductRows = (category) => {
    return formData.products[category].map((product, index) => (
      <tr key={`${category}-${index}`} className="border-b border-gray-300">
        <td className="border-r border-gray-300 p-2">
          {index === 0 ? (
            <span className="font-bold">{product.description}</span>
          ) : (
            <input
              type="text"
              value={product.description}
              onChange={(e) => handleProductChange(category, index, 'description', e.target.value)}
              className="w-full p-1 border-0"
            />
          )}
        </td>
        <td className="border-r border-gray-300 p-2 text-center">
          {product.coverage}
        </td>
        <td className="border-r border-gray-300 p-2">
          <input
            type="text"
            value={product.jobSqFt}
            onChange={(e) => handleProductChange(category, index, 'jobSqFt', e.target.value)}
            className="w-full p-1 border-0"
          />
        </td>
        <td className="border-r border-gray-300 p-2">
          <input
            type="text"
            value={product.materialNeeded}
            onChange={(e) => handleProductChange(category, index, 'materialNeeded', e.target.value)}
            className="w-full p-1 border-0"
          />
        </td>
        <td className="border-r border-gray-300 p-2">
          <div className="flex items-center">
            <span className="text-red-500 mr-1">$</span>
            <input
              type="text"
              value={product.costPerProduct}
              onChange={(e) => handleProductChange(category, index, 'costPerProduct', e.target.value)}
              className="w-full p-1 border-0"
            />
          </div>
        </td>
        <td className="border-r border-gray-300 p-2 text-center">
          {product.optionalCost}
        </td>
        <td className="p-2">
          <div className="flex flex-col">
            <div className="font-bold">{product.totalNeeded.value}</div>
            <div className="flex items-center justify-center">
              <input
                type="text"
                value={product.totalNeeded.amount}
                onChange={(e) => handleTotalNeededChange(category, index, 'amount', e.target.value)}
                className="w-16 p-1 border border-gray-300 mr-1 text-right"
              />
              <span>{product.totalNeeded.unit}</span>
            </div>
          </div>
        </td>
      </tr>
    ));
  };

  const handleDownload = () => {
    // Create a printable version of the form content
    const formContent = `
      ${createCompanyHeader()}
      <h1 class="form-title">ALX Material Template - Standard Finish</h1>
      
      <div class="form-section">
        <div class="form-row">
          <div class="form-col">
            ${formatFormField('Customer', formData.customer)}
          </div>
        </div>
        <div class="form-row">
          <div class="form-col">
            ${formatFormField('Job Name', formData.jobName)}
          </div>
        </div>
        <div class="form-row">
          <div class="form-col">
            ${formatFormField('Job Address', formData.jobAddress)}
          </div>
        </div>
        <div class="form-row">
          <div class="form-col">
            ${formatFormField('Job Number', formData.jobNumber)}
          </div>
        </div>
      </div>
      
      <div class="form-section">
        <h2>Template Instructions:</h2>
        <p><strong>Step 1:</strong> Enter the total square footage of the project at the bottom of the template.</p>
        <p><strong>Step 2:</strong> Enter the cost per unit (single kit, bag etc.) for each product in the indicated column.</p>
        <p><strong>NOTE:</strong> For installation instructions please refer to the system specification sheets posted on our website. Training videos for a variety of our systems are also available on our website.</p>
        <p><a href="http://www.westcoat.com">www.westcoat.com</a></p>
      </div>
      
      <div class="form-section">
        <table class="form-table">
          <thead>
            <tr>
              <th>Product Description</th>
              <th>Coverage will vary</th>
              <th>Job (sq.ft.)</th>
              <th>Material Needed</th>
              <th>Step 2: Cost for Each Product</th>
              <th>Optional Cost (per sq. ft.)</th>
              <th>Total Material Needed</th>
            </tr>
          </thead>
          <tbody>
            ${Object.entries(formData.products).map(([category, products]) => 
              products.map((product, index) => `
                <tr>
                  <td>${product.description || ''}</td>
                  <td>${product.coverage || ''}</td>
                  <td>${product.jobSqFt || ''}</td>
                  <td>${product.materialNeeded || ''}</td>
                  <td>$${product.costPerProduct || ''}</td>
                  <td>${product.optionalCost || ''}</td>
                  <td>${product.totalNeeded.value || ''} ${product.totalNeeded.amount || ''} ${product.totalNeeded.unit || ''}</td>
                </tr>
              `).join('')
            ).join('')}
            <tr>
              <td colspan="7"><strong>Step 1: Total Square Footage</strong> ${formData.totalSquareFootage || ''}</td>
            </tr>
          </tbody>
        </table>
      </div>
      
      <div class="form-section">
        <h2>Total Costs</h2>
        <table class="form-table">
          <tbody>
            ${Object.entries(formData.totalCosts).map(([key, value]) => `
              <tr>
                <td>${key}</td>
                <td>$${value || ''}</td>
              </tr>
            `).join('')}
            <tr>
              <td><strong>Total</strong></td>
              <td><strong>$${formData.grandTotal.toFixed(2)}</strong></td>
            </tr>
          </tbody>
        </table>
      </div>
      
      <div class="form-section">
        <h2>This Sheet to Be Used as Rough Estimate Only</h2>
        <ul>
          <li>Quantities and prices are based on single bag/single gallon units. (Unless otherwise stated)</li>
          <li>Coating accessories and system options are not figured into estimates.</li>
          <li>Contact your local distributor for a price quote, specification sheets and/or dvds.</li>
          <li>We do not guarantee coverages, please allow additional material for waste.</li>
          <li>All coverage rates should be verified and adjusted for each project.</li>
        </ul>
      </div>
    `;
    
    // Print the form
    printPermitForm(formData, 'ALX Material Template', formContent);
  };

  return (
    <div className="permit-form-shell bg-white p-6 rounded-lg max-w-7xl mx-auto relative">
      <form onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 gap-4 mb-6">
          <div className="grid grid-cols-3 gap-4">
            <div className="col-span-1">
              <div className="flex">
                <label className="w-32 font-bold">Customer:</label>
                <input
                  type="text"
                  value={formData.customer}
                  onChange={(e) => handleChange('customer', e.target.value)}
                  className="flex-1 border border-gray-300 p-1"
                />
              </div>
            </div>
            <div className="col-span-2 text-right">
              <span className="text-gray-500">#REF!</span>
            </div>
          </div>
          
          <div className="grid grid-cols-3 gap-4">
            <div className="col-span-1">
              <div className="flex">
                <label className="w-32 font-bold">Job Name:</label>
                <input
                  type="text"
                  value={formData.jobName}
                  onChange={(e) => handleChange('jobName', e.target.value)}
                  className="flex-1 border border-gray-300 p-1"
                />
              </div>
            </div>
            <div className="col-span-2 text-right">
              <span className="text-gray-500">#REF!</span>
            </div>
          </div>
          
          <div className="grid grid-cols-3 gap-4">
            <div className="col-span-1">
              <div className="flex">
                <label className="w-32 font-bold">Job Address:</label>
                <input
                  type="text"
                  value={formData.jobAddress}
                  onChange={(e) => handleChange('jobAddress', e.target.value)}
                  className="flex-1 border border-gray-300 p-1"
                />
              </div>
            </div>
            <div className="col-span-2 text-right">
              <span className="text-gray-500">#REF!</span>
            </div>
          </div>
          
          <div className="grid grid-cols-3 gap-4">
            <div className="col-span-1">
              <div className="flex">
                <label className="w-32 font-bold">Job Number:</label>
                <input
                  type="text"
                  value={formData.jobNumber}
                  onChange={(e) => handleChange('jobNumber', e.target.value)}
                  className="flex-1 border border-gray-300 p-1"
                />
              </div>
            </div>
            <div className="col-span-2 text-right">
              <span className="text-gray-500">#REF!</span>
            </div>
          </div>
        </div>

        {/* Logo section - commented out for now */}
        {/* 
        <div className="mb-6 border-t border-b border-gray-300 py-4">
          <div className="flex items-center">
            <div className="w-1/3">
              <img 
                src="/path-to-westcoat-logo.jpg" 
                alt="Westcoat" 
                className="h-12"
              />
            </div>
            <div className="w-2/3 text-center text-2xl font-bold">
              ALX Material Template - Standard Finish
            </div>
          </div>
        </div>
        */}

        <div className="mb-6 border-t border-b border-gray-300 py-4">
          <div className="grid grid-cols-4 gap-4">
            <div className="col-span-1 border-r border-gray-300 pr-2">
              {/* Westcoat logo */}
              <div className="bg-black text-white p-4 flex items-center justify-center h-full">
                <div className="text-xl font-bold">westcoat</div>
              </div>
            </div>
            <div className="col-span-3 flex items-center justify-center">
              <div className="text-center text-2xl font-bold">
                ALX Material Template - Standard Finish
              </div>
            </div>
          </div>
        </div>

        <div className="mb-6 grid grid-cols-4 gap-4">
          <div className="col-span-1">
            <div className="font-bold mb-2">Template Instructions:</div>
            <div className="mb-4">
              <div className="font-bold">Step 1:</div>
              <div>Enter the total square footage of the project at the bottom of the template.</div>
            </div>
            <div className="mb-4">
              <div className="font-bold">Step 2:</div>
              <div>Enter the cost per unit (single kit, bag etc.) for each product in the indicated column.</div>
            </div>
            <div className="mb-4">
              <div className="font-bold">NOTE:</div>
              <div>For installation instructions please refer to the system specification sheets posted on our website. Training videos for a variety of our systems are also available on our website.</div>
              <div className="text-blue-500 underline">
                <a href="http://www.westcoat.com" target="_blank" rel="noopener noreferrer">www.westcoat.com</a>
              </div>
            </div>
          </div>
          
          <div className="col-span-3">
            <table className="w-full border-collapse">
              <thead>
                <tr className="bg-gray-100">
                  <th className="border border-gray-300 p-2 text-left">Product Description</th>
                  <th className="border border-gray-300 p-2 text-center">Coverage will vary</th>
                  <th className="border border-gray-300 p-2 text-center">Job (sq.ft.)</th>
                  <th className="border border-gray-300 p-2 text-center">Material Needed</th>
                  <th className="border border-gray-300 p-2 text-center text-red-500">Step 2: Cost for Each Product</th>
                  <th className="border border-gray-300 p-2 text-center">Optional Cost (per sq. ft.)</th>
                  <th className="border border-gray-300 p-2 text-center">
                    <div className="text-center font-bold">Total Material</div>
                    <div className="text-center font-bold">Needed</div>
                  </th>
                </tr>
              </thead>
              <tbody>
                {renderProductRows('sheetMembrane')}
                {renderProductRows('baseCoat')}
                {renderProductRows('slurryCoat')}
                {renderProductRows('textureCoat')}
                {renderProductRows('topCoat')}
                <tr className="bg-red-100">
                  <td colSpan="7" className="p-2 text-red-600 font-bold border border-gray-300">
                    Step 1: Total Square Footage
                    <input
                      type="text"
                      value={formData.totalSquareFootage}
                      onChange={(e) => handleChange('totalSquareFootage', e.target.value)}
                      className="ml-4 p-1 border border-gray-300 w-32"
                    />
                  </td>
                </tr>
              </tbody>
            </table>

            <div className="text-center italic mt-2">
              Please read the complete specification sheet prior to ordering material or beginning job.
            </div>

            <div className="grid grid-cols-3 gap-4 mt-6">
              <div className="col-span-2"></div>
              <div className="col-span-1">
                <div className="border border-gray-300 mb-4">
                  <div className="text-center font-bold border-b border-gray-300 p-2">Total Costs</div>
                  
                  <table className="w-full">
                    <tbody>
                      {Object.entries(formData.totalCosts).map(([key, value]) => (
                        <tr key={key} className="border-b border-gray-300">
                          <td className="p-2 font-bold">{key}</td>
                          <td className="p-2">
                            <div className="flex items-center">
                              <span className="mr-1">$</span>
                              <input
                                type="text"
                                value={value}
                                onChange={(e) => handleTotalCostChange(key, e.target.value)}
                                className="w-24 p-1 border border-gray-300 text-right"
                              />
                            </div>
                          </td>
                        </tr>
                      ))}
                      <tr>
                        <td className="p-2 font-bold">Total</td>
                        <td className="p-2">
                          <div className="flex items-center">
                            <span className="mr-1">$</span>
                            <span className="font-bold">{formData.grandTotal.toFixed(2)}</span>
                          </div>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                
                <div className="text-center text-sm italic">
                  Rounding is not reflected in above price
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="mb-6 border border-gray-300 p-4">
          <div className="text-center font-bold italic mb-4">
            This Sheet to Be Used as Rough Estimate Only
          </div>
          
          <ul className="list-disc pl-6 space-y-2">
            <li>Quantities and prices are based on single bag/single gallon units. (Unless otherwise stated)</li>
            <li>Coating accessories and system options are not figured into estimates.</li>
            <li>Contact your local distributor for a price quote, specification sheets and/or dvds.</li>
            <li>We do not guarantee coverages, please allow additional material for waste.</li>
            <li>All coverage rates should be verified and adjusted for each project.</li>
          </ul>
        </div>

        {/* Add padding at the bottom to ensure content isn't hidden behind the floating bar */}
        <div className="pb-20"></div>
      </form>
      
      {/* Floating Action Bar */}
      <FloatingActionBar 
        onCancel={onClose}
        onDownload={handleDownload}
        onSubmit={handleSubmit}
        loading={loading}
        isUpdate={!!(currentPermit && currentPermit._id)}
      />
    </div>
  );
};

export default ALXMaterialForm;
